import * as React from 'react';
import { useMemo, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { RouteTemplates } from '../navigation/routes';
import RaterApprovalDashboard from './RaterApprovalDashboard';
import { useCurrentUser } from '../auth/hooks';
import Account from '../account/Account';
import CustomerService from '../customerService/CustomerService';
import { useDispatch } from 'react-redux';
import { raterApprovalParticipantsAction } from './api/raterApprovalParticipantsAction';
import { useAppState } from '../core/redux/hooks';
import { Dispatch } from 'redux';
import { useWindowSize } from '../core/hooks/useWindowSize';
import { useAnyLoading, useAnyError } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { PAGE_BG, PAGE_HEIGHT } from '../layout/pageStyle';
import { FLEX_COLUMN, FLEX_CENTERED } from '../layout/flexBox';
import { Spinner } from '../loading/Spinner';
import { COLORS } from '../globalStyle/colors';
import ErrorMsg from '../error/ErrorMsg';
import { Str } from '../localization/translationKeys';
import { css } from '@emotion/core';
import { logOutAction } from '../auth/login/api/logoutAction';
import { useMount } from '../core/hooks/useMount';
import { PortalType } from '../navigation/portalType';
import { setPortalTypeAction } from '../core/redux/setPortalTypeAction';
import ChangePasswordModal from '../account/ChangePasswordModal';
import ChangeSecurityQuestionModal from '../account/ChangeSecurityQuestionModal';


const styles = {
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


const loadParticipantInfo = (
  dispatch: Dispatch,
) => (
  approverId: number | undefined,
  hasParticipants: boolean,
) => {
  if (approverId === undefined) {
    return;
  }

  if (!hasParticipants) {
    dispatch(raterApprovalParticipantsAction(approverId));
  }
};


/**
 * RaterApprovalProjectRouter: a component that routes to pages in the rater approver portal experience.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalRouter: React.FC = () => {
  const { isMobile } = useWindowSize();
  const dispatch     = useDispatch();
  const currentUser  = useCurrentUser();

  useMount(() => { dispatch(setPortalTypeAction(PortalType.RATER_APPROVAL)); });

  if (!currentUser?.hasRaterApprovalLoginPermission) {
    dispatch(logOutAction(isMobile));
  }

  const participants = useAppState(state => state.raterApprovalParticipants);

  const approverId = useMemo(() => currentUser?.personId, [currentUser]);

  const actionTypes = [
    ActionType.RaterApprovalParticipantsRequest,
    ActionType.RaterApprovalRatersRequest,
  ];

  const isLoading = useAnyLoading(actionTypes);
  const hasError  = useAnyError(actionTypes);

  const loadParticipants = React.useCallback(
    () => loadParticipantInfo(dispatch)(approverId, !!participants),
    [approverId, dispatch, participants],
  );

  useEffect(loadParticipants, [approverId]);

  if (isLoading || hasError) {
    return (
      <div css={[PAGE_BG, PAGE_HEIGHT, FLEX_COLUMN, FLEX_CENTERED]}>
        {
          isLoading
            ? <Spinner fill={COLORS.GREEN} css={styles.spinner} />
            : <ErrorMsg error={Str.LoadFailed} showRetry={true} onRetry={loadParticipants} />
        }
      </div>
    );
  }

  return (
    <Switch>
      <Route path={RouteTemplates.RaterApprovalAccountConfig} component={Account} />
      <Route path={RouteTemplates.RaterApprovalCustomerService} component={CustomerService} />
      <Route path={RouteTemplates.RaterApprovalChangePassword} component={ChangePasswordModal} />
      <Route path={RouteTemplates.RaterApprovalChangeSecurityQuestion} component={ChangeSecurityQuestionModal} />
      <Route path={RouteTemplates.RaterApprovalDashboard} component={RaterApprovalDashboard} />
    </Switch>
  );
};

export default RaterApprovalRouter;
